import React, {Fragment, ReactElement, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    InputAdornment,
    Pagination,
    TextField,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {
    ProfileApiGet,
    ProfileFilter,
    useDeleteProfileByIdMutation,
    useGetProfileQuery,
    usePutProfileByIdMutation,
} from "./users_api";
import {SearchBox} from "../../components/SearchBox";
import sortComparator from "../../libs/sortComparator";
import filterFunction from "../../libs/filterFunction";
import pager from "../../libs/pager";
import {Route, Routes, useHref} from "react-router-dom";
import {UserCreate} from "./UserCreate";
import {UserEdit} from "./UserEdit";
import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";
import usePaginator from "../../hooks/usePaginator";
import userPlaceholder from "/assets/imgs/user.jpg"
import {ListCardDataCol, ListGrid} from "../../components/styled";
import useDownloadImage from "../storage/useDownloadImage";
import ImageUploadDialog from "../storage/ImageUploadDialog";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import {supabase} from "../../supabase";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Upload from "@mui/icons-material/Upload";
import  Password  from "@mui/icons-material/Password";
import  Add  from "@mui/icons-material/Add";

export type ProfileItemProps = {
    onDelete: (id: string) => void,
    item: ProfileApiGet,
    selectable?: boolean;
    onSelect?: (id: string) => void;

}


interface ResetPasswordDialogProps {
    uid: string;
    onClose: () => void;
}

const ResetPasswordDialog = ({onClose, uid}: ResetPasswordDialogProps) => {

    const [password, setPassword] = useState("")
    const [reveal, setReveal] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined)

    const handleReset = async () => {
        setError(undefined);
        const {error} = await supabase.rpc("reset_password", {uid, password})
        if (error) {
            setError(error.message)
        }
        onClose();
    }

    return <Dialog open={true} onClose={onClose} maxWidth={"xs"} fullWidth={true}>
        <DialogContent>
            {error && <Alert severity={"error"}>{error}</Alert>}
            <Typography variant={"body2"}>Geslo</Typography>
            <TextField
                sx={{width: "100%"}}
                placeholder={"Geslo"}
                value={password}
                autoCapitalize="none"
                autoComplete="password"
                type={reveal ? "text" : "password"}
                onChange={(e) => setPassword(e.target.value)}
                slotProps={{
                    input: {

                        endAdornment: <InputAdornment position="end">
                            <IconButton
                                tabIndex={-1}
                                aria-label={
                                    reveal ? 'hide the password' : 'display the password'
                                }
                                onClick={() => setReveal(!reveal)}
                                edge="end"
                            >
                                {reveal ? <VisibilityOff/> : <Visibility/>}
                            </IconButton>
                        </InputAdornment>
                    }
                }}/>
        </DialogContent>
        <DialogActions>
            <Button disabled={password.length < 8} type="submit" onClick={handleReset}>Ok</Button>
            <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
    </Dialog>
}

const ProfileItem = (props: ProfileItemProps) => {
    const {item, selectable, onSelect, onDelete} = props;
    const [reset, setReset] = useState(false)
    const [put, {
        isLoading: isUpdating,
        error,
        isError,
    }] = usePutProfileByIdMutation();
    const [uploading, setUploading] = useState(false)

    const onUpload = (path: string) => {
        put({body: {avatarUrl: path}, id: item.id})
        setUploading(false)
    }

    const path = useHref(`./${item.id}/edit`);
    const avatarUrl = useDownloadImage("avatars", item.avatarUrl)
    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card onClick={() => {
        selectable && onSelect && onSelect(item.id);
    }} sx={{
        display: "flex"
    }}>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={avatarUrl || userPlaceholder}
            alt={item.fullName}
        />
        <ListCardDataCol>
            <CardContent sx={{width: "100%"}}>
                <Typography variant={"h6"}>{item.fullName || item.email}</Typography>
                <Typography variant={"body1"}>{item.email}</Typography>
                {item.worker &&
                    <Typography variant={"body1"}>{item.worker?.firstName + " " + item.worker?.lastName}</Typography>}

                {item.userRoles?.roles?.map((value) => <Typography variant={"body2"} key={value}>
                        {value === "Foreman" && "Izmenovodja"}
                        {value === "Admin" && "Administrator"}
                        {value === "Worker" && "Delavec"}
                        {value === "Analytics" && "Analitik"}
                        {value === "AllRegs" && "Vse Registracije"}
                    </Typography>
                )}

            </CardContent>
            {!selectable && <CardActions>
                <Button
                    onClick={() => setReset(true)}
                    title={"Uredi uporabnika"}
                >
                    <Password fontSize={"small"}/>
                </Button>
                <Button
                    onClick={() => setUploading(true)}
                    title={"Naloži sliko"}
                >
                    <Upload fontSize={"small"}/>
                </Button>
                <Button
                    href={path}
                    title={"Uredi uporabnika"}
                >
                    <Edit fontSize={"small"}/>
                </Button>
                <Button
                    title="Briši uporabnika"
                    onClick={() => onDelete(item.id)}
                >
                    <Delete fontSize={"small"}/>
                </Button>
            </CardActions>}
        </ListCardDataCol>
        <ImageUploadDialog
            uploading={uploading}
            onUpload={onUpload}
            bucket={"avatars"}
            onClose={() => setUploading(false)}/>
        {reset && <ResetPasswordDialog onClose={() => setReset(false)} uid={item.id}/>}
    </Card>
}


export type ProfileListProps = {
    list: ProfileApiGet[],
    selectable?: boolean;
    onSelect?: (id: string) => void;
}

export const ProfileList = (props: ProfileListProps) => {
    const {list, onSelect, selectable} = props;

    const [deleteProfile, {
        error: deleteProfileError
    }] = useDeleteProfileByIdMutation();

    const handleDelete = (id: string) => {
        deleteProfile({id});
    }

    return <ListGrid>
        {deleteProfileError && <SnackError error={deleteProfileError}/>}

        {list.map(param => <ProfileItem key={param.id}
                                        item={param}
                                        onSelect={onSelect}
                                        selectable={selectable}
                                        onDelete={handleDelete}/>
        )}

    </ListGrid>
}


type ProfileControllerProps = {
    pageSize?: number;
    selectable?: boolean;
    onSelect?: (id: string) => void;
    customFilter?: ReactElement
}

const sortFields = {
    email: "Email",
    fullName: "Ime",
    Active: "Aktiven",
}
export const ProfileListController = function ({pageSize, selectable, onSelect}: ProfileControllerProps) {

    const {
        filter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("users", sortFields,)

    const f: ProfileFilter = {};
    f.deleted = false;

    const {data, error, isFetching} = useGetProfileQuery(f);

    const path = useHref("./create");
    if (error) {
        return <SnackError error={error}/>;
    }
    if (isFetching || !data) {
        return <CircularProgress/>
    }

    const list: Array<ProfileApiGet> = data && data.filter(filterFunction(filter, ["email", "fullName"]))
        .sort(sortComparator(sortDirection, sortField)) || [];
    const [pages, currentPage] = pager(list, page, pageSize);

    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>
            {!selectable && <Button startIcon={<Add/>} href={path}>Dodaj</Button>}
            <Box sx={{display: "flex"}}>
                <SearchBox value={filter} onChange={setFilter}/>
                <SortBox sortDirection={sortDirection} sortField={sortField}
                         onChangeDirection={setSortDirection}
                         onSelectField={setSortField} fields={sortFields}
                />
            </Box>
        </Toolbar>
        {error && <SnackError error={error}/>}

        <ProfileList list={currentPage} selectable={selectable} onSelect={onSelect}/>
        <Pagination page={page} onChange={(e, page) => {
            setPage(page)
            console.log("onchange")
        }} count={pages}/>
    </Fragment>

}
export type ProfileProps = {
    pageSize?: number;
}

export default ({pageSize = 15}: ProfileProps) => {

    return <Routes>
        <Route path={"/create"} element={<UserCreate/>}/>
        <Route path={"/:id/edit"} element={<UserEdit/>}/>
        <Route path={"/"} element={<Fragment>
            <Title title={"Upravljanje uporabnikov"}/>
            <ProfileListController pageSize={pageSize}/>
        </Fragment>}/>
    </Routes>
}
